// To book › Asked to move (F-08), what it does: the open requests (visitRequests.list), live from the
// stream, and what someone here does with one: keep the visit, cancel it, close a request whose visit is
// cancelled already, or move it to one of their picks (appointments.reschedule), each closing the request
// (visitRequests.resolve). Riverstone's section (VisitRequests.tsx) and Blueprint's (AskedToMove.tsx)
// draw it.
import { useEffect, useState } from 'react';
import { RpcError } from '@flowology/client';
import { client } from '../api.ts';
import { DAY_WINDOWS, zonedInstant } from '../book-model.ts';
import { liveStream } from '../connection/live.ts';
import { VISIT_REQUEST_EVENTS, type VisitRequest } from './tobook-model.ts';

export const requestError = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function useVisitRequests(onChanged: () => void) {
  const [list, setList] = useState<VisitRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    client
      .call<{ requests: VisitRequest[] }>('visitRequests.list', {})
      .then((r) => {
        setList(r.requests);
        setError(null);
      })
      .catch((e: unknown) => setError(requestError(e)));
  }, [tick]);
  useEffect(() => {
    const es = liveStream();
    let timer: ReturnType<typeof setTimeout> | undefined;
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (!VISIT_REQUEST_EVENTS.test(type)) return;
      clearTimeout(timer);
      timer = setTimeout(() => setTick((t) => t + 1), 250);
    });
    return () => {
      clearTimeout(timer);
      es.close();
    };
  }, []);

  const done = (said: string) => {
    setToast(said);
    setTick((t) => t + 1);
    onChanged();
  };
  const resolve = async (r: VisitRequest, outcome: 'moved' | 'kept' | 'cancelled') =>
    client.call('visitRequests.resolve', { id: r.id, outcome });
  const act = async (r: VisitRequest, run: () => Promise<string>) => {
    setBusy(r.id);
    setError(null);
    try {
      done(await run());
    } catch (e) {
      setError(requestError(e));
    } finally {
      setBusy(null);
    }
  };
  const keep = (r: VisitRequest) =>
    act(r, async () => {
      await resolve(r, 'kept');
      return `Kept ${r.customer.name}’s visit as booked. Let them know.`;
    });
  /** Cancelled already (on its job sheet, say): only the request is left to close. */
  const closeCancelled = (r: VisitRequest) =>
    act(r, async () => {
      await resolve(r, 'cancelled');
      return `Closed ${r.customer.name}’s request: the visit is cancelled.`;
    });
  // the job's only visit: the job is cancelled instead, on its sheet (the refusal says so)
  const cancelVisit = (r: VisitRequest) =>
    act(r, async () => {
      await client.call('appointments.cancel', {
        appointmentId: r.visit.appointmentId,
        reason: r.note ? `The customer asked to cancel: “${r.note}”` : 'The customer asked to cancel.',
      });
      await resolve(r, 'cancelled');
      return `Cancelled ${r.customer.name}’s visit. Let them know.`;
    });
  const moved = (r: VisitRequest, when: string) =>
    done(`Moved ${r.customer.name}’s visit to ${when}. Call or text them to confirm.`);
  return { list, error, toast, busy, keep, closeCancelled, cancelVisit, moved };
}

/** "Move it": the visit to a day and window (one of their picks, or another), then the request is closed. */
export async function moveVisit(
  request: VisitRequest,
  date: string,
  win: number,
  timeZone: string,
  idempotencyKey: string,
): Promise<void> {
  const [s0, e0] = DAY_WINDOWS[win]!;
  const length = Date.parse(request.visit.endsAt) - Date.parse(request.visit.startsAt);
  const start = zonedInstant(date, s0, timeZone);
  await client.call(
    'appointments.reschedule',
    {
      appointmentId: request.visit.appointmentId,
      start,
      end: new Date(Date.parse(start) + Math.max(length, (e0 - s0) * 3_600_000)).toISOString(),
      arrivalWindow: { start, end: zonedInstant(date, e0, timeZone) },
    },
    { idempotencyKey },
  );
  await client.call('visitRequests.resolve', { id: request.id, outcome: 'moved' });
}
